/// web_ui/src/api/contracts/deviceTypes.ts


/// ------------------------------
/// FE -> BE. Device input format to register a new device.
/// ------------------------------

export type RegisterDeviceInput = {
  deviceName: string;
  capacityLiters: number;
  shellyServer: string;
  shellyAuthKey: string;
  shellyDeviceId: string;
  shellyChannel: string;
  dueTimesLocal: string[];

  pricing: {
    energy: {
      model: "spot" | "fixed";
      peakPrice?: number;
      offpeakPrice?: number;
      peakStart?: string;     // "HH:MM"
      offpeakStart?: string;  // "HH:MM"
    };

    transmission: {
      peakPrice?: number;
      offpeakPrice?: number;
      peakStart?: string;
      offpeakStart?: string;
    };

    markup?: {
      peakPrice: number;
      offpeakPrice: number;
      peakStart?: string;
      offpeakStart?: string;
    };
  };
};

/// ------------------------------
/// Pricing models
/// ------------------------------

export type EnergyPricing = {
  model: "spot" | "fixed";

  // spot
  markup?: number;

  // fixed
  peakPrice?: number;
  offpeakPrice?: number;

  peakStart?: string;
  offpeakStart?: string;
};

export type TransmissionPricing = {
  peakPrice?: number;
  offpeakPrice?: number;

  peakStart?: string;
  offpeakStart?: string;
};

export type DevicePricing = {
  energy: EnergyPricing;
  transmission: TransmissionPricing;
};


/// ------------------------------
/// BE snake to FE camel conversion
/// ------------------------------

export type SessionDeviceDto = {
  device_id: number;
  device_name: string;
  capacity_liters: number;
  due_times_local: string[];
  relay_state: boolean;

  pricing: {
    energy: {
      model: "spot" | "fixed";

      markup?: number;

      peak_price?: number;
      offpeak_price?: number;

      peak_start?: string;
      offpeak_start?: string;
    };

    transmission: {
      peak_price?: number;
      offpeak_price?: number;

      peak_start?: string;
      offpeak_start?: string;
    };
  };
};

export type SessionDevice = {
  deviceId: number;
  deviceName: string;
  capacityLiters: number;
  dueTimesLocal: string[];
  relayState: boolean;

  pricing: DevicePricing;
};

export function mapSessionDevice(dto: SessionDeviceDto): SessionDevice {
  return {
    deviceId: dto.device_id,
    deviceName: dto.device_name,
    capacityLiters: dto.capacity_liters,
    dueTimesLocal: dto.due_times_local,
    relayState: dto.relay_state,

    pricing: {
      energy: {
        model: dto.pricing.energy.model,

        markup: dto.pricing.energy.markup,

        peakPrice: dto.pricing.energy.peak_price,
        offpeakPrice: dto.pricing.energy.offpeak_price,

        peakStart: dto.pricing.energy.peak_start?.slice(0, 5),
        offpeakStart: dto.pricing.energy.offpeak_start?.slice(0, 5),
      },

      transmission: {
        peakPrice: dto.pricing.transmission.peak_price,
        offpeakPrice: dto.pricing.transmission.offpeak_price,

        peakStart:
          dto.pricing.transmission.peak_start?.slice(0, 5),

        offpeakStart:
          dto.pricing.transmission.offpeak_start?.slice(0, 5),
      },
    },
  };
}

/// ------------------------------
/// A generic Device type holding all the data about device in BE
/// ------------------------------
export type Device = {
  deviceId: string;
  deviceName: string;
  capacityLiters: number;
  shellyServer: string;
  shellyAuthKey: string;
  shellyDeviceId: string;
  shellyChannel: number;
  dueTimesLocal: string[];
  relayState: "ON" | "OFF";
};

/// ------------------------------
/// Device info from BE to render heating plan
/// ------------------------------
export type DeviceChartResponseDto = {
  success: boolean;
  region: string;
  device_id: number;
  due_times_utc: string[];
  windows: {
    start_utc: string;
    end_utc: string;
    window_consumption?: number;
  }[];
  slot_prices: {
    timestamp_utc: string;
    total_price: number;
  }[];
};

export type DeviceChartResponse = {
  success: boolean;
  region: string;
  deviceId: number;
  dueTimesUtc: string[];
  deviceChartData: {
    startUtc: string;
    endUtc: string;
    windowConsumption: number;
  }[];
  slotPrices: {
    timestampUtc: string;
    totalPrice: number;
  }[];
};

export function mapDeviceChartResponse(
  dto: DeviceChartResponseDto
): DeviceChartResponse {
  return {
    success: dto.success,
    region: dto.region,
    deviceId: dto.device_id,
    dueTimesUtc: dto.due_times_utc,

    deviceChartData: dto.windows.map((w) => ({
      startUtc: w.start_utc,
      endUtc: w.end_utc,
      windowConsumption: w.window_consumption ?? 0,
    })),

    slotPrices: dto.slot_prices.map((s) => ({
      timestampUtc: s.timestamp_utc,
      totalPrice: s.total_price,
    })),
  };
}

/// ------------------------------
/// FE -> BE. Edit device input.
/// ------------------------------

export type EditDeviceInput = {
  deviceId: number;

  deviceName: string;
  capacityLiters: number;

  shellyServer: string;
  shellyAuthKey: string;
  shellyDeviceId: string;

  shellyChannel: number;

  dueTimesLocal: string[];

  pricing: {
    energy: {
      model: "spot" | "fixed";

      // spot
      markup?: number;

      // fixed
      peakPrice?: number;
      offpeakPrice?: number;

      peakStart?: string;
      offpeakStart?: string;
    };

    transmission: {
      peakPrice?: number;
      offpeakPrice?: number;

      peakStart?: string;
      offpeakStart?: string;
    };
  };
};

export type EditDeviceResponseDto = {
  device_id: number;
  device_name: string;
  capacity_liters: number;
  due_times_utc: string[];
};

export type EditDeviceResponse = {
  deviceId: number;
  deviceName: string;
  capacityLiters: number;
  dueTimesUtc: string [];
}

export function mapEditDeviceResponse(
  dto: EditDeviceResponseDto
): EditDeviceResponse {
  return {
    deviceId: dto.device_id,
    deviceName: dto.device_name,
    capacityLiters: dto.capacity_liters,
    dueTimesUtc: dto.due_times_utc,
  };
}

